import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  useTheme,
  useMediaQuery,
  Chip,
  LinearProgress,
} from '@mui/material'
import { Description as DescriptionIcon } from '@mui/icons-material'
import SignatureCanvas from 'react-signature-canvas'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@store/index'
import { logout } from '@store/slices/authSlice'
import { AuthService } from '@/services/authService'
import { User } from '@/types'
import { ConsentService } from '@/services/consentService'
import { fetchUserIpAddress } from '@/utils/ipAddress'
import LOGO from '@/assets/pillarThera.png'

interface HippaConsentDialogProps {
  open: boolean
  user: User
  onConsentSubmitted: () => void
}

const HIPAA_CONSENT_SECTIONS = [
  {
    id: 'confidentiality',
    title: '1. Confidentiality',
    content:
      'I will keep all patient information confidential and use/disclose PHI only as allowed by law and PillarThera policy.',
  },
  {
    id: 'access-control',
    title: '2. Access Control',
    content:
      'I will use only my own credentials, enable MFA, and never share logins or leave sessions unattended.',
  },
  {
    id: 'minimum-necessary',
    title: '3. Minimum Necessary & Data Handling',
    content:
      'I will access only the minimum necessary PHI, and I will not download, export, screenshot, or share PHI outside PillarThera unless explicitly authorized and secured.',
  },
  {
    id: 'secure-devices',
    title: '4. Secure Devices & Messaging',
    content:
      'I will use updated, password-protected devices; avoid public/shared computers; and keep PHI out of unapproved channels (personal email/text).',
  },
  {
    id: 'incident-reporting',
    title: '5. Incident/Breach Reporting',
    content:
      'I will immediately (within 24 hours) report any suspected loss, unauthorized access, or disclosure of PHI to PillarThera compliance.',
  },
  {
    id: 'acknowledgement',
    title: '6. Acknowledgement & Sanctions',
    content:
      'I understand violations may result in access revocation, contractual action, and legal penalties.',
  },
]

const HippaConsentDialog: React.FC<HippaConsentDialogProps> = ({
  open,
  user,
  onConsentSubmitted,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const signaturePadRef = useRef<SignatureCanvas>(null)
  const [signatureData, setSignatureData] = useState<string | null>(null)
  const [ipAddress, setIpAddress] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isFetchingIp, setIsFetchingIp] = useState(true)
  // Track initials for each section (6 sections total)
  const [initials, setInitials] = useState<Record<string, string>>({
    confidentiality: '',
    'access-control': '',
    'minimum-necessary': '',
    'secure-devices': '',
    'incident-reporting': '',
    acknowledgement: '',
  })
  const [providerFullName, setProviderFullName] = useState(
    `${user.firstName} ${user.lastName}`
  )
  const [companyName, setCompanyName] = useState('')
  const dateOfConsent = format(new Date(), 'MM/dd/yyyy')

  const handleInitialChange = (sectionId: string, value: string) => {
    // Limit to 2 characters and uppercase
    const upperValue = value.toUpperCase().slice(0, 2)
    setInitials(prev => ({
      ...prev,
      [sectionId]: upperValue,
    }))
    // Clear error when user starts typing
    if (error && error.includes('initials')) {
      setError(null)
    }
  }

  // Calculate progress - only count the 6 sections
  const calculateProgress = () => {
    // Count completed sections (have 2-character initials)
    const completedSections = HIPAA_CONSENT_SECTIONS.filter(
      section => initials[section.id] && initials[section.id].length === 2
    )

    const totalItems = HIPAA_CONSENT_SECTIONS.length
    const completedItems = completedSections.length

    return {
      completed: completedItems,
      total: totalItems,
      percentage: totalItems > 0 ? (completedItems / totalItems) * 100 : 0,
    }
  }

  const progress = calculateProgress()

  // Helper to check if all initials match
  const checkInitialsMatch = () => {
    const initialValues = HIPAA_CONSENT_SECTIONS.map(
      section => initials[section.id]
    ).filter(val => val && val.length === 2)

    if (initialValues.length === 0) return true // No initials entered yet
    if (initialValues.length === 1) return true // Only one initial entered

    const firstInitial = initialValues[0]
    return initialValues.every(initial => initial === firstInitial)
  }

  const initialsMatch = checkInitialsMatch()

  const handleLogout = async () => {
    try {
      // Call therapist logout API
      await AuthService.therapistLogout()
    } catch (error) {
      console.error('Logout failed:', error)
      // Even if API call fails, proceed with local logout
    } finally {
      dispatch(logout())
      navigate('/provider-login')
    }
  }

  useEffect(() => {
    if (open) {
      // Fetch IP address when dialog opens
      fetchUserIpAddress()
        .then(ip => {
          setIpAddress(ip)
          setIsFetchingIp(false)
        })
        .catch(() => {
          setIpAddress('Unknown')
          setIsFetchingIp(false)
        })
    }
  }, [open])

  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear()
      setSignatureData(null)
    }
  }

  const handleSignatureEnd = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const dataURL = signaturePadRef.current.toDataURL('image/png')
      setSignatureData(dataURL)
    } else {
      setSignatureData(null)
    }
  }

  const handleSubmit = async () => {
    if (!signatureData) {
      setError('Please provide a signature')
      return
    }

    // Check that all initials are filled
    const allInitialsFilled = HIPAA_CONSENT_SECTIONS.every(
      section => initials[section.id] && initials[section.id].length === 2
    )

    if (!allInitialsFilled) {
      setError('Please provide initials for all sections')
      return
    }

    // Verify that all initials are the same
    const initialValues = HIPAA_CONSENT_SECTIONS.map(
      section => initials[section.id]
    ).filter(Boolean)

    if (initialValues.length > 0) {
      const firstInitial = initialValues[0]
      const allInitialsMatch = initialValues.every(
        initial => initial === firstInitial
      )

      if (!allInitialsMatch) {
        setError(
          'All initials must be the same. Please ensure all sections have matching initials.'
        )
        return
      }
    }

    if (!providerFullName.trim()) {
      setError('Please provide your full name')
      return
    }

    if (!companyName.trim()) {
      setError('Please provide your company name')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Hardcoded values as per requirements
      const agreementTemplateId = '686e3101582277712b6bb98c'
      const agreementVersion = 'v1.0'
      const signatureType = 'drawn'

      // Get user agent from browser
      const userAgent = navigator.userAgent

      // Map section IDs to initial numbers (1-6)
      const sectionIdToInitialNumber: Record<string, number> = {
        confidentiality: 1,
        'access-control': 2,
        'minimum-necessary': 3,
        'secure-devices': 4,
        'incident-reporting': 5,
        acknowledgement: 6,
      }

      // Build initials payload
      const initialsPayload: Record<string, string> = {}
      HIPAA_CONSENT_SECTIONS.forEach(section => {
        const initialNumber = sectionIdToInitialNumber[section.id]
        if (initialNumber && initials[section.id]) {
          initialsPayload[`initial_${initialNumber}`] = initials[section.id]
        }
      })

      await ConsentService.submitHippaConsent({
        userId: user.id,
        agreementTemplateId,
        agreementVersion,
        fullName: providerFullName.trim(),
        ipAddress,
        userAgent,
        consentTime: new Date().toISOString(),
        signatureType,
        drawnSignature: signatureData, // This is already a data URL (data:image/png;base64,...)
        ...initialsPayload,
        companyName: companyName.trim(),
      })

      onConsentSubmitted()
    } catch (err: any) {
      console.error('Error submitting consent:', err)
      setError(
        err.response?.data?.message ||
          'Failed to submit consent. Please try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  // Check if form is complete
  const allInitialsFilled = HIPAA_CONSENT_SECTIONS.every(
    section => initials[section.id] && initials[section.id].length === 2
  )
  const canSubmit =
    allInitialsFilled &&
    initialsMatch &&
    signatureData !== null &&
    providerFullName.trim() !== '' &&
    companyName.trim() !== '' &&
    !loading

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing via backdrop click or ESC key
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return
        }
      }}
      disableEscapeKeyDown
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          maxHeight: isMobile ? '100vh' : '95vh',
          borderRadius: isMobile ? 0 : 3,
          bgcolor: '#FAFBFC',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Fixed Header with Logo */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'white',
            borderBottom: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            py: { xs: 3, md: 2 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              px: { xs: 2, md: 4 },
              py: { xs: 2, md: 4 },
              position: 'relative',
            }}
          >
            {/* Left spacer - flex: 1 to balance */}
            <Box sx={{ flex: 1 }} />

            {/* Logo - Centered */}
            <Box
              component="img"
              src={LOGO}
              alt="PillarThera Logo"
              sx={{
                height: { xs: 60, md: 80 },
                width: 'auto',
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            />

            {/* Progress Chip - Right End */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Chip
                icon={<DescriptionIcon sx={{ fontSize: { xs: 16, md: 18 } }} />}
                label={`${progress.completed}/${progress.total} Complete`}
                color={progress.percentage === 100 ? 'success' : 'default'}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  fontWeight: 600,
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ pb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress.percentage}
            sx={{
              height: 8,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor:
                  progress.percentage === 100 ? 'success.main' : 'primary.main',
              },
            }}
          />
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: { xs: 2, md: 4 },
            py: { xs: 3, md: 2 },
          }}
        >
          {/* Title */}
          <Box sx={{ mb: { xs: 3, md: 4 }, textAlign: 'center' }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: 'primary.main',
                fontSize: { xs: '1.5rem', md: '2rem' },
                mb: 1,
              }}
            >
              PillarThera HIPAA Compliance Acknowledgment
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 600,
              }}
            >
              (Required)
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.875rem', md: '1rem' },
                mt: 1,
              }}
            >
              You must acknowledge these responsibilities before accessing PHI
              in PillarThera.
            </Typography>
          </Box>

          {/* Introduction */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              mb: 4,
              bgcolor: 'white',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.8,
                fontSize: { xs: '0.875rem', md: '1.2rem' },
                mb: 1,
              }}
            >
              By initialing each item and signing below, you confirm you
              understand and will follow these rules when handling Protected
              Health Information (PHI).
            </Typography>
          </Paper>

          {/* Consent Sections */}
          <Box sx={{ mb: 4 }}>
            {HIPAA_CONSENT_SECTIONS.map((section, index) => (
              <Paper
                key={section.id}
                elevation={0}
                sx={{
                  mb: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: { xs: 2, md: 3 },
                  bgcolor: 'white',
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{
                    mb: 1.5,
                    color: 'text.primary',
                    fontSize: { xs: '1rem', md: '1.125rem' },
                  }}
                >
                  {section.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.8,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    mb: 2,
                  }}
                >
                  {section.content}
                </Typography>
                {/* Initial Field */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    pt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      fontWeight: 500,
                      minWidth: 'fit-content',
                    }}
                  >
                    Initials:
                  </Typography>
                  <TextField
                    value={initials[section.id] || ''}
                    onChange={e =>
                      handleInitialChange(section.id, e.target.value)
                    }
                    placeholder="AB"
                    size="small"
                    error={
                      !initialsMatch &&
                      initials[section.id] &&
                      initials[section.id].length === 2
                    }
                    helperText={
                      !initialsMatch &&
                      initials[section.id] &&
                      initials[section.id].length === 2
                        ? 'Initials must match across all sections'
                        : ''
                    }
                    inputProps={{
                      maxLength: 2,
                      style: {
                        textAlign: 'center',
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                      },
                    }}
                    sx={{
                      width: { xs: 80, md: 100 },
                      '& .MuiInputBase-input': {
                        fontWeight: 600,
                        fontSize: { xs: '0.875rem', md: '1rem' },
                      },
                      '& .MuiFormHelperText-root': {
                        fontSize: '0.7rem',
                        m: 0,
                        mt: 0.5,
                        textAlign: 'center',
                      },
                    }}
                  />
                </Box>
              </Paper>
            ))}
          </Box>

          <Divider sx={{ my: { xs: 4, md: 5 } }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Final Signature
            </Typography>
          </Divider>

          {/* Final Signature Section */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              border: '2px solid',
              borderColor: 'primary.light',
              borderRadius: 2,
              bgcolor: 'white',
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                mb: 3,
                color: 'primary.main',
                fontSize: { xs: '1.125rem', md: '1.25rem' },
              }}
            >
              Final Signature
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 3,
                lineHeight: 1.8,
                fontSize: { xs: '0.875rem', md: '1rem' },
                color: 'text.secondary',
              }}
            >
              I certify that:
            </Typography>

            <Box
              sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  lineHeight: 1.8,
                }}
              >
                • I have read and understand all sections of this Intake &
                Consent Packet.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  lineHeight: 1.8,
                }}
              >
                • I have had the opportunity to ask questions.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  lineHeight: 1.8,
                }}
              >
                • I agree to all terms and authorize therapy and related
                processes.
              </Typography>
            </Box>

            {/* Provider Signature */}
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    Provider Full Name
                  </Typography>
                  <TextField
                    disabled
                    value={providerFullName}
                    placeholder="Enter your full name"
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.875rem', md: '1rem' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    Date
                  </Typography>
                  <TextField
                    value={dateOfConsent}
                    disabled
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'text.primary',
                        fontWeight: 500,
                        fontSize: { xs: '0.875rem', md: '1rem' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    Company Name{' '}
                    <Typography component="span" sx={{ color: 'error.main' }}>
                      *
                    </Typography>
                  </Typography>
                  <TextField
                    value={companyName}
                    onChange={e => {
                      setCompanyName(e.target.value)
                      setError(null)
                    }}
                    placeholder="Enter company name"
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: { xs: '0.875rem', md: '1rem' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    Signature (draw below){' '}
                    <Typography component="span" sx={{ color: 'error.main' }}>
                      *
                    </Typography>
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      border: '2px solid',
                      borderColor: signatureData ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      p: 1,
                      bgcolor: 'white',
                      display: 'flex',
                      justifyContent: 'center',
                      overflow: 'auto',
                      '& canvas': {
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        cursor: 'crosshair',
                        maxWidth: '100%',
                        height: 'auto',
                      },
                    }}
                  >
                    <SignatureCanvas
                      ref={signaturePadRef}
                      canvasProps={{
                        width: isMobile ? 300 : 500,
                        height: 150,
                        className: 'signature-canvas',
                        style: { maxWidth: '100%', height: 'auto' },
                      }}
                      onEnd={handleSignatureEnd}
                    />
                  </Paper>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearSignature}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    Clear Signature
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: { xs: 2, md: 3 },
          bgcolor: 'white',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <Button
          variant="contained"
          onClick={handleLogout}
          size="large"
          sx={{
            minWidth: { xs: 'auto', md: 150 },
            py: 1.5,
            fontSize: { xs: '0.875rem', md: '1rem' },
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            bgcolor: 'error.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'error.dark',
            },
          }}
        >
          Cancel & Log Out
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          size="large"
          sx={{
            minWidth: { xs: '100%', md: 200 },
            py: 1.5,
            fontSize: { xs: '0.875rem', md: '1rem' },
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            boxShadow: canSubmit
              ? '0 4px 12px rgba(25, 118, 210, 0.3)'
              : 'none',
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'I Agree & Continue'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default HippaConsentDialog
